import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChecklistData, ChecklistCategory, ChecklistItem, ItemStatus, ItemTag, ChecklistEvidence, ChecklistAuditEntry } from '../types';
import { saveChecklistItem, fetchChecklist, uploadEvidence, fetchEvidence, deleteEvidence, authFetch, signChecklist, voidChecklist, fetchChecklistAudit } from '../api';
import SupplierDraftModal from './SupplierDraftModal';

/* ── localStorage persistence ─────────────────────────────────── */

const LS_CHECKLISTS_KEY = 'ch-checklists';

interface StoredChecklist {
  id: string;
  data: ChecklistData;
  updatedAt: string;
}

function loadStoredChecklists(): StoredChecklist[] {
  try {
    const raw = localStorage.getItem(LS_CHECKLISTS_KEY);
    return raw ? (JSON.parse(raw) as StoredChecklist[]) : [];
  } catch {
    return [];
  }
}

function saveChecklist(id: string, data: ChecklistData): void {
  try {
    const existing = loadStoredChecklists();
    const idx = existing.findIndex((c) => c.id === id);
    const entry: StoredChecklist = { id, data, updatedAt: new Date().toISOString() };
    if (idx >= 0) {
      existing[idx] = entry;
    } else {
      existing.push(entry);
    }
    // Keep max 50 checklists
    const trimmed = existing.slice(-50);
    localStorage.setItem(LS_CHECKLISTS_KEY, JSON.stringify(trimmed));
  } catch {
    /* quota error — ignore */
  }
}

function loadChecklist(id: string): ChecklistData | null {
  const stored = loadStoredChecklists().find((c) => c.id === id);
  return stored?.data ?? null;
}

/* ── Helpers: normalise legacy items (boolean checked → status) ─ */

function normaliseItem(item: ChecklistItem): ChecklistItem {
  // If the item already has a valid status field, keep it
  if (item.status && ['unchecked', 'pass', 'fail', 'na'].includes(item.status)) {
    return {
      ...item,
      notes: item.notes ?? '',
      tag: item.tag ?? parseTag(item.text),
      text: stripTagPrefix(item.text),
    };
  }
  // Legacy boolean → status migration
  const status: ItemStatus = item.checked ? 'pass' : 'unchecked';
  return {
    ...item,
    status,
    notes: item.notes ?? '',
    checked: item.checked ?? false,
    tag: item.tag ?? parseTag(item.text),
    text: stripTagPrefix(item.text),
  };
}

function parseTag(text: string): ItemTag | undefined {
  if (/^\[MANDATORY\]/i.test(text.trim())) return 'mandatory';
  if (/^\[GUIDANCE\]/i.test(text.trim())) return 'guidance';
  return undefined;
}

function stripTagPrefix(text: string): string {
  return text.replace(/^\[(MANDATORY|GUIDANCE)\]\s*/i, '').trim();
}

function normaliseData(data: ChecklistData): ChecklistData {
  return {
    ...data,
    categories: data.categories.map((cat) => ({
      ...cat,
      items: cat.items.map(normaliseItem),
    })),
  };
}

/* ── Status helpers ───────────────────────────────────────────── */

const STATUS_LABELS: Record<ItemStatus, string> = {
  unchecked: '—',
  pass: '✅',
  fail: '❌',
  na: '➖',
};

const STATUS_SHORT: Record<ItemStatus, string> = {
  unchecked: '—',
  pass: 'Pass',
  fail: 'Fail',
  na: 'N/A',
};

function statusForExport(status: ItemStatus): string {
  switch (status) {
    case 'pass': return 'x';
    case 'fail': return '!';
    case 'na': return '-';
    default: return ' ';
  }
}

/* ── Export helpers ──────────────────────────────────────────── */

function checklistToMarkdown(data: ChecklistData): string {
  const lines: string[] = [`# ${data.title}`, ''];

  if (data.coverage) {
    lines.push(`> 📊 Coverage: ${data.coverage.domains}/${data.coverage.totalDomains} domains · ${data.coverage.clausesMatched} clauses matched`);
    lines.push('');
  }

  const { totalItems, assessed } = computeProgress(data);
  lines.push(`Progress: ${assessed}/${totalItems} items assessed`);
  lines.push('');

  for (const cat of data.categories) {
    lines.push(`## ${cat.name}`, '');
    for (const item of cat.items) {
      const check = statusForExport(item.status);
      const tag = item.tag === 'mandatory' ? '[MANDATORY] ' : item.tag === 'guidance' ? '[GUIDANCE] ' : '';
      const ref = item.standard && item.clause
        ? ` (${item.standard}, Clause ${item.clause})`
        : item.standard
          ? ` (${item.standard})`
          : '';
      lines.push(`- [${check}] ${tag}${item.text}${ref}`);
      if (item.notes) {
        lines.push(`  > Note: ${item.notes}`);
      }
    }
    lines.push('');
  }

  lines.push('---');
  lines.push('⚠️ This is a research summary, not a formal compliance checklist.');
  lines.push('Verify all requirements with a qualified professional before relying on this information for compliance purposes.');

  return lines.join('\n');
}

function exportAsMarkdown(data: ChecklistData): void {
  const md = checklistToMarkdown(data);
  const blob = new Blob([md], { type: 'text/markdown' });
  const filename = `${data.title.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.md`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Export Metadata ─────────────────────────────────────────── */

interface ExportMetadata {
  projectName: string;
  siteAddress: string;
  inspectorName: string;
  inspectionDate: string;
  buildingClass: string;
}

/* ── Export HTML helpers ─────────────────────────────────────── */

function escHtml(str: string): string {
  return (str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatExportDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

type EvidenceExportMap = Record<string, Array<{ dataUrl: string; caption: string; filename: string }>>;

function generateExportHtml(data: ChecklistData, meta: ExportMetadata, evidenceExport?: EvidenceExportMap): string {
  const { totalItems, assessed, passCount, failCount, naCount } = computeProgress(data);
  const uncheckedCount = totalItems - assessed;
  const progressPct = totalItems > 0 ? Math.round((assessed / totalItems) * 100) : 0;
  const generatedAt = new Date().toLocaleString('en-AU', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  // Project metadata rows
  const metaRows: string[] = [];
  if (meta.projectName) metaRows.push(`<tr><td>Project</td><td>${escHtml(meta.projectName)}</td></tr>`);
  if (meta.siteAddress) metaRows.push(`<tr><td>Site Address</td><td>${escHtml(meta.siteAddress)}</td></tr>`);
  if (meta.inspectorName) metaRows.push(`<tr><td>Prepared by</td><td>${escHtml(meta.inspectorName)}</td></tr>`);
  if (meta.inspectionDate) metaRows.push(`<tr><td>Date</td><td>${formatExportDate(meta.inspectionDate)}</td></tr>`);
  if (meta.buildingClass) metaRows.push(`<tr><td>Building Class</td><td>${escHtml(meta.buildingClass)}</td></tr>`);
  const projectDetailsHtml = metaRows.length
    ? `<table class="meta-table">${metaRows.join('')}</table>`
    : '';

  // Coverage block
  const coverageHtml = data.coverage
    ? `<div class="coverage-bar"><span class="coverage-label">&#128202; Coverage:</span> ${data.coverage.domains}/${data.coverage.totalDomains} domains &middot; ${data.coverage.clausesMatched} clauses matched</div>`
    : '';

  // Category tables
  const categoriesHtml = data.categories.map((cat, catIdx) => {
    const catAssessed = cat.items.filter((i) => i.status !== 'unchecked').length;
    const rows = cat.items.map((item, itemIdx) => {
      const statusText = item.status === 'pass' ? '&#10003; Pass'
        : item.status === 'fail' ? '&#10007; Fail'
        : item.status === 'na' ? '&mdash; N/A'
        : '&#9675; Pending';
      const tagBadge = item.tag === 'mandatory'
        ? '<span class="tag-req">Required</span>'
        : item.tag === 'guidance'
          ? '<span class="tag-rec">Recommended</span>'
          : '';
      const clauseRef = item.standard && item.clause
        ? `${escHtml(item.standard)} &sect;${escHtml(item.clause)}`
        : item.standard ? escHtml(item.standard) : '&mdash;';
      const notesHtml = item.notes
        ? `<div class="item-notes">&#128221; ${escHtml(item.notes)}</div>`
        : '';
      const evData = item.id && evidenceExport?.[item.id];
      const evidenceHtml = evData && evData.length > 0
        ? `<div class="item-evidence"><span class="ev-label">&#128247; Evidence (${evData.length}):</span><div class="ev-thumbs">${
            evData.map((ev) => `<img class="ev-thumb" src="${ev.dataUrl}" alt="${escHtml(ev.caption || ev.filename)}" title="${escHtml(ev.caption || ev.filename)}">`).join('')
          }</div></div>`
        : '';
      const rowClass = item.status === 'pass' ? 'row-pass'
        : item.status === 'fail' ? 'row-fail'
        : item.status === 'na' ? 'row-na' : '';
      return `<tr class="${rowClass}"><td class="td-num">${catIdx + 1}.${itemIdx + 1}</td><td class="td-item">${tagBadge}${escHtml(item.text)}${notesHtml}${evidenceHtml}</td><td class="td-ref">${clauseRef}</td><td class="td-status"><span class="sb sb--${item.status}">${statusText}</span></td></tr>`;
    }).join('');
    return `<div class="cat-block"><div class="cat-hdr"><span class="cat-name">${escHtml(cat.name)}</span><span class="cat-cnt">${catAssessed}/${cat.items.length} assessed</span></div><table class="cl-tbl"><colgroup><col style="width:38px"><col><col style="width:140px"><col style="width:74px"></colgroup><thead><tr><th>#</th><th>Requirement</th><th>Standard / Clause</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table></div>`;
  }).join('');

  // F3: Signature block
  let signatureBlockHtml = '';
  if (data.signedBy) {
    const signedAtStr = data.signedAt
      ? new Date(data.signedAt).toLocaleString('en-AU', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : '';
    const isVoided = !!data.voidReason;
    const voidAtStr = data.voidAt
      ? new Date(data.voidAt).toLocaleString('en-AU', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : '';
    signatureBlockHtml = `<div class="sig-block${isVoided ? ' sig-void' : ''}">
<div class="sig-block-title">${isVoided ? '<span class="sig-void-stamp">⚠️ VOIDED</span> ' : '✅ '}Digital Sign-Off</div>
<div class="sig-row"><span class="sig-lbl">Signed by:</span> ${escHtml(data.signedBy)}</div>
${signedAtStr ? `<div class="sig-row"><span class="sig-lbl">Date:</span> ${escHtml(signedAtStr)}</div>` : ''}
${data.signedComment ? `<div class="sig-row"><span class="sig-lbl">Comment:</span> ${escHtml(data.signedComment)}</div>` : ''}
${isVoided ? `<div class="sig-row sig-row--void"><span class="sig-lbl">Voided:</span> ${escHtml(data.voidReason!)}</div>` : ''}
${isVoided && voidAtStr ? `<div class="sig-row sig-row--void"><span class="sig-lbl">Voided at:</span> ${escHtml(voidAtStr)}</div>` : ''}
</div>`;
  }

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${escHtml(data.title)}</title>
<style>
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:-apple-system,"Helvetica Neue",Arial,sans-serif;font-size:10pt;color:#1a2035;background:#fff;line-height:1.5}
.page-wrap{max-width:210mm;margin:0 auto;padding:16mm 15mm 20mm}
.print-bar{display:flex;gap:10px;justify-content:flex-end;margin-bottom:20px}
.btn-print{background:#1e3a5f;color:#fff;border:none;padding:8px 18px;border-radius:6px;font-size:10pt;font-weight:600;cursor:pointer;font-family:inherit}
.btn-print:hover{background:#2a4d7f}
.doc-header{border-bottom:3px solid #1e3a5f;padding-bottom:14px;margin-bottom:14px}
.doc-title{font-size:18pt;font-weight:700;color:#1e3a5f;margin-bottom:3px;line-height:1.2}
.doc-sub{font-size:9.5pt;color:#64748b}
.meta-table{width:100%;border-collapse:collapse;font-size:9pt;margin:12px 0}
.meta-table td{padding:4px 8px;border:1px solid #e2e8f0}
.meta-table td:first-child{font-weight:600;color:#475569;background:#f7fafc;width:130px}
.sum-grid{display:flex;gap:8px;margin:14px 0;flex-wrap:wrap}
.sum-card{flex:1;min-width:78px;border:1px solid #e2e8f0;border-radius:6px;padding:10px 12px;text-align:center}
.sum-card .num{font-size:22pt;font-weight:700;line-height:1;display:block}
.sum-card .lbl{font-size:7.5pt;color:#94a3b8;text-transform:uppercase;letter-spacing:.05em;display:block;margin-top:4px}
.c-tot{border-color:#93c5fd}.c-tot .num{color:#1d4ed8}
.c-pass{border-color:#86efac}.c-pass .num{color:#15803d}
.c-fail{border-color:#fca5a5}.c-fail .num{color:#b91c1c}
.c-na{border-color:#e2e8f0}.c-na .num{color:#64748b}
.c-pend{border-color:#fcd34d}.c-pend .num{color:#92400e}
.prog-wrap{margin:6px 0 14px}
.prog-lbl{font-size:9pt;color:#64748b;margin-bottom:5px}
.prog-bar{height:6px;background:#f1f5f9;border-radius:3px;overflow:hidden}
.prog-fill{height:100%;background:linear-gradient(90deg,#15803d,#4ade80);border-radius:3px}
.coverage-bar{font-size:9pt;color:#1d4ed8;background:#eff6ff;border-left:3px solid #3b82f6;padding:6px 10px;margin:8px 0 14px;border-radius:0 4px 4px 0}
.coverage-label{font-weight:600;margin-right:4px}
.cat-block{margin-bottom:20px;page-break-inside:avoid}
.cat-hdr{display:flex;align-items:center;justify-content:space-between;background:#1e3a5f;color:#fff;padding:8px 12px;border-radius:4px 4px 0 0}
.cat-name{font-weight:600;font-size:10pt}
.cat-cnt{font-size:8.5pt;opacity:.85}
.cl-tbl{width:100%;border-collapse:collapse;font-size:9pt}
.cl-tbl th{background:#f8fafc;font-size:7.5pt;font-weight:700;text-transform:uppercase;letter-spacing:.04em;padding:5px 8px;border:1px solid #e2e8f0;color:#475569;text-align:left}
.cl-tbl td{padding:6px 8px;border:1px solid #e2e8f0;vertical-align:top}
.td-num{text-align:center;color:#94a3b8;font-size:8.5pt}
.td-ref{font-size:8.5pt;color:#475569}
.td-status{text-align:center}
tr.row-pass td{background:#f0fdf4}
tr.row-fail td{background:#fff1f2}
tr.row-na td{background:#f9fafb;color:#6b7280}
.sb{display:inline-block;padding:2px 6px;border-radius:4px;font-size:8pt;font-weight:600;white-space:nowrap}
.sb--pass{background:#dcfce7;color:#15803d}
.sb--fail{background:#fee2e2;color:#b91c1c}
.sb--na{background:#f1f5f9;color:#64748b}
.sb--unchecked{background:#fef9c3;color:#92400e}
.tag-req{display:inline-block;background:#fff1f2;color:#be123c;border:1px solid #fda4af;font-size:7pt;font-weight:700;padding:1px 5px;border-radius:3px;margin-right:5px;vertical-align:middle}
.tag-rec{display:inline-block;background:#fffbeb;color:#b45309;border:1px solid #fcd34d;font-size:7pt;font-weight:700;padding:1px 5px;border-radius:3px;margin-right:5px;vertical-align:middle}
.item-notes{font-size:8pt;color:#64748b;font-style:italic;margin-top:4px;padding-top:3px;border-top:1px dashed #e2e8f0}
.item-evidence{margin-top:6px;font-size:8pt;color:#475569}
.ev-label{font-weight:600;margin-right:6px}
.ev-thumbs{display:flex;flex-wrap:wrap;gap:4px;margin-top:4px}
.ev-thumb{width:80px;height:80px;object-fit:cover;border:1px solid #e2e8f0;border-radius:4px}
.disclaimer{border:1px solid #fecaca;background:#fff1f2;color:#b91c1c;padding:10px 14px;border-radius:6px;font-size:8.5pt;margin:20px 0 12px;line-height:1.5}
.sig-block{border:2px solid #16a34a;background:#f0fdf4;border-radius:6px;padding:12px 16px;margin:16px 0;font-size:9pt}
.sig-block-title{font-weight:700;color:#15803d;font-size:10pt;margin-bottom:8px}
.sig-row{margin:3px 0;color:#1a2035}
.sig-lbl{font-weight:600;color:#475569;min-width:100px;display:inline-block}
.sig-void{border-color:#b91c1c;background:#fff1f2}
.sig-void-stamp{display:inline-block;background:#b91c1c;color:#fff;font-weight:700;font-size:9pt;padding:2px 10px;border-radius:3px;margin-bottom:8px;letter-spacing:.06em}
.sig-row--void{color:#b91c1c}
.doc-footer{font-size:8pt;color:#94a3b8;text-align:center;margin-top:14px;padding-top:8px;border-top:1px solid #e2e8f0}
@page{size:A4;margin:18mm 14mm 22mm}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}.page-wrap{max-width:100%;padding:0}.print-bar{display:none!important}.cat-block{page-break-inside:avoid}}
</style>
</head>
<body>
<div class="page-wrap">
<div class="print-bar"><button class="btn-print" onclick="window.print()">&#128424;&#65039; Print / Save as PDF</button></div>
<div class="doc-header"><h1 class="doc-title">${escHtml(data.title)}</h1><p class="doc-sub">Compliance Checklist</p></div>
${projectDetailsHtml}
<div class="sum-grid">
<div class="sum-card c-tot"><span class="num">${totalItems}</span><span class="lbl">Total Items</span></div>
<div class="sum-card c-pass"><span class="num">${passCount}</span><span class="lbl">Pass</span></div>
<div class="sum-card c-fail"><span class="num">${failCount}</span><span class="lbl">Fail</span></div>
<div class="sum-card c-na"><span class="num">${naCount}</span><span class="lbl">N/A</span></div>
<div class="sum-card c-pend"><span class="num">${uncheckedCount}</span><span class="lbl">Pending</span></div>
</div>
<div class="prog-wrap"><div class="prog-lbl">${assessed} of ${totalItems} items assessed (${progressPct}%)</div><div class="prog-bar"><div class="prog-fill" style="width:${progressPct}%"></div></div></div>
${coverageHtml}
${categoriesHtml}
${signatureBlockHtml}
<div class="disclaimer"><strong>&#9888;&#65039; Research Summary Only.</strong> This checklist is a research aid generated from ingested standards. It is not a formal compliance assessment. Verify all requirements against the officially published standards and seek advice from a qualified professional before relying on this document for compliance purposes.</div>
<div class="doc-footer">Compliance Hub &middot; Generated ${generatedAt}</div>
</div>
</body>
</html>`;
}

/** Fetch an evidence image and return a base64 dataURL for embedding in HTML exports. */
async function evidenceToDataUrl(url: string): Promise<string | null> {
  try {
    const res = await authFetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** Build a map of item.id → [{dataUrl, caption, filename}] for PDF export. */
async function buildEvidenceExportMap(
  data: ChecklistData,
  evidenceMap: Record<string, ChecklistEvidence[]>,
): Promise<EvidenceExportMap> {
  const result: EvidenceExportMap = {};
  const allItems: Array<{ id: string; evList: ChecklistEvidence[] }> = [];
  for (const cat of data.categories) {
    for (const item of cat.items) {
      if (item.id && evidenceMap[item.id]?.length) {
        allItems.push({ id: item.id, evList: evidenceMap[item.id] });
      }
    }
  }
  await Promise.allSettled(
    allItems.map(async ({ id, evList }) => {
      const converted = await Promise.allSettled(
        evList.map((ev) =>
          evidenceToDataUrl(ev.url).then((dataUrl) =>
            dataUrl ? { dataUrl, caption: ev.caption, filename: ev.filename } : null,
          ),
        ),
      );
      const entries = converted
        .filter((r): r is PromiseFulfilledResult<{ dataUrl: string; caption: string; filename: string } | null> => r.status === 'fulfilled')
        .map((r) => r.value)
        .filter((v): v is { dataUrl: string; caption: string; filename: string } => v !== null);
      if (entries.length) result[id] = entries;
    }),
  );
  return result;
}

async function triggerExport(
  data: ChecklistData,
  meta: ExportMetadata,
  mode: 'download' | 'print',
  evidenceMap?: Record<string, ChecklistEvidence[]>,
): Promise<void> {
  // Build evidence export data (embed as base64 for offline HTML)
  let evidenceExport: EvidenceExportMap | undefined;
  if (evidenceMap && Object.keys(evidenceMap).length > 0) {
    try {
      evidenceExport = await buildEvidenceExportMap(data, evidenceMap);
    } catch {
      // Non-fatal — export without evidence
    }
  }

  const html = generateExportHtml(data, meta, evidenceExport);
  const slug = (meta.projectName || data.title)
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase()
    .slice(0, 60);
  const dateStr = new Date().toISOString().slice(0, 10);
  const filename = `${slug}-compliance-checklist-${dateStr}.html`;
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);

  if (mode === 'print') {
    const w = window.open(url, '_blank');
    if (w) {
      w.addEventListener('load', () => { setTimeout(() => { w.print(); URL.revokeObjectURL(url); }, 400); });
    } else {
      URL.revokeObjectURL(url);
    }
  } else {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
}

/* ── Export Modal ────────────────────────────────────────────── */

interface ExportModalProps {
  onClose: () => void;
  onExport: (meta: ExportMetadata, mode: 'download' | 'print') => void;
}

function ExportModal({ onClose, onExport }: ExportModalProps) {
  const today = new Date().toISOString().slice(0, 10);
  const [modalMeta, setModalMeta] = useState<ExportMetadata>({
    projectName: '',
    siteAddress: '',
    inspectorName: '',
    inspectionDate: today,
    buildingClass: '',
  });

  return (
    <div className="export-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="export-modal-title">
      <div className="export-modal" onClick={(e) => e.stopPropagation()}>
        <div className="export-modal-header">
          <h3 className="export-modal-title" id="export-modal-title">Export Compliance Checklist</h3>
          <button className="export-modal-close" onClick={onClose} type="button" aria-label="Close">&#x2715;</button>
        </div>
        <p className="export-modal-hint">Project details appear in the document header only — not saved to the checklist.</p>
        <div className="export-modal-body">
          <div className="export-modal-field">
            <label htmlFor="exp-project">Project Name</label>
            <input
              id="exp-project"
              type="text"
              value={modalMeta.projectName}
              onChange={(e) => setModalMeta((p) => ({ ...p, projectName: e.target.value }))}
              placeholder="e.g. 42 Smith St Renovation"
              autoFocus
            />
          </div>
          <div className="export-modal-field">
            <label htmlFor="exp-site">Site Address</label>
            <input
              id="exp-site"
              type="text"
              value={modalMeta.siteAddress}
              onChange={(e) => setModalMeta((p) => ({ ...p, siteAddress: e.target.value }))}
              placeholder="e.g. 42 Smith Street, Brisbane QLD 4000"
            />
          </div>
          <div className="export-modal-field">
            <label htmlFor="exp-inspector">Inspector / Preparer</label>
            <input
              id="exp-inspector"
              type="text"
              value={modalMeta.inspectorName}
              onChange={(e) => setModalMeta((p) => ({ ...p, inspectorName: e.target.value }))}
              placeholder="e.g. Jane Smith"
            />
          </div>
          <div className="export-modal-field">
            <label htmlFor="exp-date">Inspection Date</label>
            <input
              id="exp-date"
              type="date"
              value={modalMeta.inspectionDate}
              onChange={(e) => setModalMeta((p) => ({ ...p, inspectionDate: e.target.value }))}
            />
          </div>
          <div className="export-modal-field">
            <label htmlFor="exp-class">Building Class</label>
            <input
              id="exp-class"
              type="text"
              value={modalMeta.buildingClass}
              onChange={(e) => setModalMeta((p) => ({ ...p, buildingClass: e.target.value }))}
              placeholder="e.g. Class 1a, Class 5"
            />
          </div>
        </div>
        <div className="export-modal-actions">
          <button className="export-modal-btn export-modal-btn--cancel" onClick={onClose} type="button">
            Cancel
          </button>
          <button className="export-modal-btn export-modal-btn--print" onClick={() => onExport(modalMeta, 'print')} type="button">
            &#128424;&#65039; Print Now
          </button>
          <button className="export-modal-btn export-modal-btn--download" onClick={() => onExport(modalMeta, 'download')} type="button">
            &#8681; Download HTML
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── F3: Sign Modal ─────────────────────────────────────────── */

interface SignModalProps {
  onClose: () => void;
  onSign: (signedBy: string, comment?: string) => void;
  busy: boolean;
  error: string | null;
}

function SignModal({ onClose, onSign, busy, error }: SignModalProps) {
  const [signedBy, setSignedBy] = useState('');
  const [comment, setComment] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!signedBy.trim()) return;
    onSign(signedBy.trim(), comment.trim() || undefined);
  };

  return (
    <div className="export-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="sign-modal-title">
      <div className="export-modal" onClick={(e) => e.stopPropagation()}>
        <div className="export-modal-header">
          <h3 className="export-modal-title" id="sign-modal-title">✍️ Sign &amp; Lock Checklist</h3>
          <button className="export-modal-close" onClick={onClose} type="button" aria-label="Close">&#x2715;</button>
        </div>
        <p className="export-modal-hint">Signing locks this checklist as read-only. An admin can void the lock if corrections are needed.</p>
        <form onSubmit={handleSubmit}>
          <div className="export-modal-body">
            <div className="export-modal-field">
              <label htmlFor="sign-name">Your Name <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                id="sign-name"
                type="text"
                value={signedBy}
                onChange={(e) => setSignedBy(e.target.value)}
                placeholder="e.g. Jane Smith"
                autoFocus
                required
              />
            </div>
            <div className="export-modal-field">
              <label htmlFor="sign-comment">Comment (optional)</label>
              <input
                id="sign-comment"
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="e.g. Final inspection sign-off"
              />
            </div>
            {error && (
              <div className="checklist-upload-error" role="alert" style={{ marginTop: 8 }}>
                ⚠️ {error}
              </div>
            )}
          </div>
          <div className="export-modal-actions">
            <button className="export-modal-btn export-modal-btn--cancel" type="button" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              className="export-modal-btn export-modal-btn--download"
              type="submit"
              disabled={busy || !signedBy.trim()}
            >
              {busy ? 'Signing…' : '✅ Sign & Lock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── F3: Void Modal ─────────────────────────────────────────── */

interface VoidModalProps {
  onClose: () => void;
  onVoid: (reason: string) => void;
  busy: boolean;
  error: string | null;
}

function VoidModal({ onClose, onVoid, busy, error }: VoidModalProps) {
  const [reason, setReason] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    onVoid(reason.trim());
  };

  return (
    <div className="export-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="void-modal-title">
      <div className="export-modal" onClick={(e) => e.stopPropagation()}>
        <div className="export-modal-header">
          <h3 className="export-modal-title" id="void-modal-title">⚠️ Void Signature</h3>
          <button className="export-modal-close" onClick={onClose} type="button" aria-label="Close">&#x2715;</button>
        </div>
        <p className="export-modal-hint">Voiding removes the lock and allows editing again. A reason is required and recorded in the audit log.</p>
        <form onSubmit={handleSubmit}>
          <div className="export-modal-body">
            <div className="export-modal-field">
              <label htmlFor="void-reason">Reason <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                id="void-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Corrections required after re-inspection"
                autoFocus
                required
              />
            </div>
            {error && (
              <div className="checklist-upload-error" role="alert" style={{ marginTop: 8 }}>
                ⚠️ {error}
              </div>
            )}
          </div>
          <div className="export-modal-actions">
            <button className="export-modal-btn export-modal-btn--cancel" type="button" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              className="export-modal-btn export-modal-btn--download"
              type="submit"
              disabled={busy || !reason.trim()}
              style={{ background: '#b91c1c' }}
            >
              {busy ? 'Voiding…' : '⚠️ Void & Unlock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Lightbox Modal ─────────────────────────────────────────── */

interface LightboxProps {
  evidenceList: ChecklistEvidence[];
  index: number;
  itemId: string;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onDelete: (itemId: string, evidenceId: string) => void;
  /** F3: when true, hide the delete button (checklist is locked) */
  readOnly?: boolean;
}

function LightboxModal({ evidenceList, index, itemId, onClose, onPrev, onNext, onDelete, readOnly }: LightboxProps) {
  const ev = evidenceList[index];
  if (!ev) return null;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') onPrev();
    if (e.key === 'ArrowRight') onNext();
  };

  return (
    <div
      className="ev-lightbox-overlay"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label="Photo evidence"
      tabIndex={-1}
    >
      <div className="ev-lightbox" onClick={(e) => e.stopPropagation()}>
        <div className="ev-lightbox-header">
          <span className="ev-lightbox-counter">{index + 1} / {evidenceList.length}</span>
          {ev.caption && <span className="ev-lightbox-caption">{ev.caption}</span>}
          <span className="ev-lightbox-filename">{ev.filename}</span>
          <div className="ev-lightbox-actions">
            {!readOnly && (
              <button
                className="ev-lightbox-delete"
                onClick={() => onDelete(itemId, ev.id)}
                title="Delete photo"
                type="button"
              >
                🗑️
              </button>
            )}
            <button
              className="ev-lightbox-close"
              onClick={onClose}
              title="Close"
              type="button"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="ev-lightbox-img-wrap">
          {evidenceList.length > 1 && (
            <button
              className="ev-lightbox-nav ev-lightbox-nav--prev"
              onClick={onPrev}
              type="button"
              aria-label="Previous"
            >
              ‹
            </button>
          )}
          <img
            className="ev-lightbox-img"
            src={ev.url}
            alt={ev.caption || ev.filename || 'Evidence photo'}
          />
          {evidenceList.length > 1 && (
            <button
              className="ev-lightbox-nav ev-lightbox-nav--next"
              onClick={onNext}
              type="button"
              aria-label="Next"
            >
              ›
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Progress computation ───────────────────────────────────── */

function computeProgress(data: ChecklistData) {
  let totalItems = 0;
  let assessed = 0;
  let passCount = 0;
  let failCount = 0;
  let naCount = 0;
  for (const cat of data.categories) {
    totalItems += cat.items.length;
    for (const item of cat.items) {
      if (item.status !== 'unchecked') assessed++;
      if (item.status === 'pass') passCount++;
      if (item.status === 'fail') failCount++;
      if (item.status === 'na') naCount++;
    }
  }
  return { totalItems, assessed, passCount, failCount, naCount };
}

/* ── Props ──────────────────────────────────────────────────── */

interface Props {
  /** Unique ID for persistence (typically queryId or msgId) */
  checklistId: string;
  /** Structured checklist from the backend */
  data: ChecklistData;
  /** Optional: fire a follow-up query for a standard reference */
  onCrossRef?: (query: string) => void;
}

/* ── Component ──────────────────────────────────────────────── */

export default function Checklist({ checklistId, data: initialData, onCrossRef }: Props) {
  // If the data has rawMarkdown and no categories (parse fallback), render raw
  const isFallback = (!initialData.categories || initialData.categories.length === 0) && !!initialData.rawMarkdown;

  // Restore saved state or use initial data (normalised)
  const [checklistData, setChecklistData] = useState<ChecklistData>(() => {
    const saved = loadChecklist(checklistId);
    if (saved) return normaliseData(saved);
    return normaliseData(initialData);
  });

  // Export modal visibility
  const [showExportModal, setShowExportModal] = useState(false);
  // Export generating (async evidence embedding)
  const [exportGenerating, setExportGenerating] = useState(false);

  // F3: Sign/Void/Audit state
  const isAdmin = localStorage.getItem('standards-user-role') === 'admin';
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [signBusy, setSignBusy] = useState(false);
  const [voidBusy, setVoidBusy] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [auditEntries, setAuditEntries] = useState<ChecklistAuditEntry[]>([]);
  const [auditExpanded, setAuditExpanded] = useState(false);

  // F3: derived lock state
  const isLocked = checklistData.locked === true;

  // F2: Evidence state
  const [evidenceMap, setEvidenceMap] = useState<Record<string, ChecklistEvidence[]>>({});
  const [uploadingItems, setUploadingItems] = useState<Set<string>>(new Set());
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ itemId: string; evidenceList: ChecklistEvidence[]; index: number } | null>(null);
  const [supplierDraftItem, setSupplierDraftItem] = useState<{ itemId: string; itemText: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadRef = useRef<{ catIdx: number; itemIdx: number } | null>(null);
  // Track which checklist we've loaded evidence for
  const evidenceFetchedForId = useRef<string | null>(null);

  // Collapsed state per category
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // Track which items have their notes expanded
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());

  // Persist on every change (localStorage — fast fallback)
  useEffect(() => {
    if (!isFallback) {
      saveChecklist(checklistId, checklistData);
    }
  }, [checklistId, checklistData, isFallback]);

  // D1 server-side sync: fetch from server to get item IDs when checklistId is available
  const d1Fetched = useRef(false);
  useEffect(() => {
    if (d1Fetched.current || isFallback) return;
    // If we have a checklistId (server-assigned), fetch to get item IDs
    const serverChecklistId = checklistData.checklistId || (checklistId && checklistId.length > 20 ? checklistId : null);
    if (!serverChecklistId) return;
    d1Fetched.current = true;
    // Small delay to allow background D1 save to complete
    const timer = setTimeout(async () => {
      try {
        const serverData = await fetchChecklist(serverChecklistId);
        if (serverData && serverData.categories?.length > 0) {
          // Merge server item IDs + F3 sign/lock state into current state
          setChecklistData((prev) => {
            const merged = {
              ...prev,
              checklistId: serverData.checklistId,
              // F3: always take sign/lock state from server (authoritative)
              locked: serverData.locked,
              signedBy: serverData.signedBy,
              signedAt: serverData.signedAt,
              signedComment: serverData.signedComment,
              voidReason: serverData.voidReason,
              voidBy: serverData.voidBy,
              voidAt: serverData.voidAt,
            };
            merged.categories = prev.categories.map((cat, ci) => {
              const serverCat = serverData.categories[ci];
              if (!serverCat) return cat;
              return {
                ...cat,
                items: cat.items.map((item, ii) => {
                  const serverItem = serverCat.items[ii];
                  if (!serverItem?.id) return item;
                  // Keep local status/notes if user changed them, but get the server ID
                  return { ...item, id: serverItem.id };
                }),
              };
            });
            return merged;
          });
          // F3: load audit entries if checklist has been signed
          if (serverData.signedAt || serverData.voidAt) {
            fetchChecklistAudit(serverChecklistId).then(setAuditEntries).catch(() => {});
          }
        }
      } catch {
        // Non-fatal — D1 sync is best-effort
      }
    }, 1500); // Wait 1.5s for background D1 save
    return () => clearTimeout(timer);
  }, [checklistId, checklistData.checklistId, isFallback]);

  // F2: Load evidence for all items after D1 sync gives us item IDs
  useEffect(() => {
    const clId = checklistData.checklistId;
    if (!clId || evidenceFetchedForId.current === clId || isFallback) return;

    // Wait until at least some items have server IDs
    let hasItemIds = false;
    for (const cat of checklistData.categories) {
      for (const item of cat.items) {
        if (item.id) { hasItemIds = true; break; }
      }
      if (hasItemIds) break;
    }
    if (!hasItemIds) return;

    evidenceFetchedForId.current = clId;

    // Fetch evidence for all items with IDs concurrently
    for (const cat of checklistData.categories) {
      for (const item of cat.items) {
        if (item.id) {
          const itemId = item.id;
          fetchEvidence(clId, itemId)
            .then((list) => {
              if (list.length > 0) {
                setEvidenceMap((prev) => ({ ...prev, [itemId]: list }));
              }
            })
            .catch(() => {});
        }
      }
    }
  }, [checklistData.checklistId, checklistData.categories, isFallback]);

  // Debounce timer ref for notes persistence
  const notesTimerRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Compute progress
  const { totalItems, assessed, passCount, failCount, naCount } = useMemo(
    () => computeProgress(checklistData),
    [checklistData],
  );

  const progressPct = totalItems > 0 ? (assessed / totalItems) * 100 : 0;

  // Set item status (immediate D1 persistence)
  const setItemStatus = useCallback((catIdx: number, itemIdx: number, status: ItemStatus) => {
    setChecklistData((prev) => {
      const newCats = prev.categories.map((cat, ci) => {
        if (ci !== catIdx) return cat;
        const newItems = cat.items.map((item, ii) => {
          if (ii !== itemIdx) return item;
          // Toggle: if clicking the same status, revert to unchecked
          const newStatus = item.status === status ? 'unchecked' : status;
          // Persist to D1 immediately if we have server IDs
          const clId = prev.checklistId;
          if (clId && item.id) {
            saveChecklistItem(clId, item.id, newStatus).catch(() => {});
          }
          return {
            ...item,
            status: newStatus,
            checked: newStatus === 'pass', // keep legacy field in sync
          };
        });
        return { ...cat, items: newItems };
      });
      return { ...prev, categories: newCats };
    });
  }, []);

  // Set item notes (debounced D1 persistence)
  const setItemNotes = useCallback((catIdx: number, itemIdx: number, notes: string) => {
    setChecklistData((prev) => {
      const newCats = prev.categories.map((cat, ci) => {
        if (ci !== catIdx) return cat;
        const newItems = cat.items.map((item, ii) => {
          if (ii !== itemIdx) return item;
          // Debounce D1 save for notes (800ms)
          const timerKey = `${catIdx}-${itemIdx}`;
          const clId = prev.checklistId;
          if (clId && item.id) {
            if (notesTimerRef.current[timerKey]) {
              clearTimeout(notesTimerRef.current[timerKey]);
            }
            notesTimerRef.current[timerKey] = setTimeout(() => {
              saveChecklistItem(clId, item.id!, undefined, notes).catch(() => {});
              delete notesTimerRef.current[timerKey];
            }, 800);
          }
          return { ...item, notes };
        });
        return { ...cat, items: newItems };
      });
      return { ...prev, categories: newCats };
    });
  }, []);

  // Toggle notes expansion
  const toggleNotes = useCallback((key: string) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Collapse/expand category
  const toggleCollapse = useCallback((catName: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(catName)) next.delete(catName);
      else next.add(catName);
      return next;
    });
  }, []);

  // Export handler (async — may need to fetch evidence images for embedding)
  const handleExport = useCallback(async (meta: ExportMetadata, mode: 'download' | 'print') => {
    setShowExportModal(false);
    setExportGenerating(true);
    try {
      await triggerExport(checklistData, meta, mode, evidenceMap);
    } finally {
      setExportGenerating(false);
    }
  }, [checklistData, evidenceMap]);

  // F2: Camera/upload handlers
  const handleCameraClick = useCallback((catIdx: number, itemIdx: number) => {
    pendingUploadRef.current = { catIdx, itemIdx };
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, []);

  const handleFileSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const pending = pendingUploadRef.current;
    if (!file || !pending) return;

    const { catIdx, itemIdx } = pending;
    pendingUploadRef.current = null;

    const item = checklistData.categories[catIdx]?.items[itemIdx];
    const clId = checklistData.checklistId;
    if (!item?.id || !clId) {
      setUploadError('Checklist must be saved before attaching photos.');
      return;
    }
    const itemId = item.id;

    setUploadingItems((prev) => new Set([...prev, itemId]));
    setUploadError(null);
    try {
      const newEvidence = await uploadEvidence(clId, itemId, file);
      setEvidenceMap((prev) => ({
        ...prev,
        [itemId]: [...(prev[itemId] ?? []), newEvidence],
      }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setUploadError(msg);
    } finally {
      setUploadingItems((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  }, [checklistData]);

  const handleEvidenceDelete = useCallback(async (itemId: string, evidenceId: string) => {
    const clId = checklistData.checklistId;
    if (!clId) return;
    if (!window.confirm('Delete this photo? This cannot be undone.')) return;
    try {
      await deleteEvidence(clId, evidenceId);
      setEvidenceMap((prev) => ({
        ...prev,
        [itemId]: (prev[itemId] ?? []).filter((e) => e.id !== evidenceId),
      }));
      // Close lightbox if it was showing deleted item
      setLightbox((prev) => {
        if (prev && prev.itemId === itemId) {
          const remaining = (evidenceMap[itemId] ?? []).filter((e) => e.id !== evidenceId);
          if (remaining.length === 0) return null;
          const newIndex = Math.min(prev.index, remaining.length - 1);
          return { ...prev, evidenceList: remaining, index: newIndex };
        }
        return prev;
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      setUploadError(msg);
    }
  }, [checklistData.checklistId, evidenceMap]);

  // Reset all
  const resetAll = useCallback(() => {
    if (!window.confirm('Reset all items to unchecked? This cannot be undone.')) return;
    setChecklistData((prev) => ({
      ...prev,
      categories: prev.categories.map((cat) => ({
        ...cat,
        items: cat.items.map((item) => ({ ...item, status: 'unchecked' as ItemStatus, checked: false, notes: '' })),
      })),
    }));
  }, []);

  // F3: Sign handler
  const handleSign = useCallback(async (signedBy: string, comment?: string) => {
    const clId = checklistData.checklistId;
    if (!clId) return;
    setSignBusy(true);
    setSignError(null);
    try {
      const updated = await signChecklist(clId, signedBy, comment);
      setChecklistData((prev) => ({
        ...prev,
        locked: updated.locked,
        signedBy: updated.signedBy,
        signedAt: updated.signedAt,
        signedComment: updated.signedComment,
      }));
      setSignModalOpen(false);
      // Refresh audit
      fetchChecklistAudit(clId).then(setAuditEntries).catch(() => {});
    } catch (err) {
      setSignError(err instanceof Error ? err.message : 'Sign failed');
    } finally {
      setSignBusy(false);
    }
  }, [checklistData.checklistId]);

  // F3: Void handler
  const handleVoid = useCallback(async (reason: string) => {
    const clId = checklistData.checklistId;
    if (!clId) return;
    setVoidBusy(true);
    setVoidError(null);
    try {
      const updated = await voidChecklist(clId, reason);
      setChecklistData((prev) => ({
        ...prev,
        locked: updated.locked,
        voidReason: updated.voidReason,
        voidBy: updated.voidBy,
        voidAt: updated.voidAt,
      }));
      setVoidModalOpen(false);
      // Refresh audit
      fetchChecklistAudit(clId).then(setAuditEntries).catch(() => {});
    } catch (err) {
      setVoidError(err instanceof Error ? err.message : 'Void failed');
    } finally {
      setVoidBusy(false);
    }
  }, [checklistData.checklistId]);

  /* ── Fallback: raw markdown rendering ────────────────────── */
  if (isFallback) {
    return (
      <div className="checklist checklist--fallback">
        <div className="checklist-fallback-banner">
          ⚠️ Couldn't generate an interactive checklist. Showing results as text.
        </div>
        <div className="checklist-fallback-content">
          {(initialData.rawMarkdown ?? '').split('\n').map((line, i) => {
            // Render checklist-style lines with visual checkboxes
            const checkMatch = line.match(/^[-*]\s*\[([x ])\]\s*(.*)$/i);
            if (checkMatch) {
              const isChecked = checkMatch[1].toLowerCase() === 'x';
              return (
                <div key={i} className="checklist-fallback-item">
                  <span className={`checklist-fallback-checkbox${isChecked ? ' checklist-fallback-checkbox--checked' : ''}`}>
                    {isChecked ? '✅' : '☐'}
                  </span>
                  <span>{checkMatch[2]}</span>
                </div>
              );
            }
            // Heading lines
            if (line.startsWith('### ')) return <h4 key={i} className="checklist-fallback-h3">{line.replace(/^###\s+/, '')}</h4>;
            if (line.startsWith('## ')) return <h3 key={i} className="checklist-fallback-h2">{line.replace(/^##\s+/, '')}</h3>;
            if (line.startsWith('# ')) return <h2 key={i} className="checklist-fallback-h1">{line.replace(/^#\s+/, '')}</h2>;
            // Bullet points
            if (line.match(/^[-*]\s+/)) return <div key={i} className="checklist-fallback-bullet">• {line.replace(/^[-*]\s+/, '')}</div>;
            // Empty lines
            if (!line.trim()) return <br key={i} />;
            // Normal text
            return <p key={i} className="checklist-fallback-p">{line}</p>;
          })}
        </div>
        <p className="checklist-disclaimer">
          ⚠️ This is a research summary, not a formal compliance checklist.
          Verify all requirements with a qualified professional before relying on this information for compliance purposes.
        </p>
      </div>
    );
  }

  /* ── Coverage indicator ──────────────────────────────────── */
  const coverage = checklistData.coverage;
  const coveragePct = coverage ? (coverage.domains / Math.max(coverage.totalDomains, 1)) * 100 : null;

  return (
    <div className={`checklist${isLocked ? ' checklist--locked' : ''}`}>
      {/* Header */}
      <div className="checklist-header">
        <h3 className="checklist-title">{checklistData.title}</h3>
        <div className="checklist-actions">
          <button
            className="checklist-action-btn"
            onClick={() => setShowExportModal(true)}
            title="Export as PDF or HTML"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9V2h12v7" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Export PDF
          </button>
          <button
            className="checklist-action-btn"
            onClick={() => exportAsMarkdown(checklistData)}
            title="Export as Markdown"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            Markdown
          </button>
          {/* F3: Sign button — shown when checklist is saved and not locked */}
          {checklistData.checklistId && !isLocked && (
            <button
              className="checklist-action-btn checklist-action-btn--sign"
              onClick={() => { setSignError(null); setSignModalOpen(true); }}
              title="Sign and lock this checklist"
              type="button"
            >
              ✍️ Sign
            </button>
          )}
          {/* F3: Void button — admin only, shown when locked */}
          {isAdmin && isLocked && (
            <button
              className="checklist-action-btn checklist-action-btn--void"
              onClick={() => { setVoidError(null); setVoidModalOpen(true); }}
              title="Void signature (admin only)"
              type="button"
            >
              ⚠️ Void
            </button>
          )}
          {/* F3: Signed badge */}
          {isLocked && checklistData.signedBy && (
            <span className="checklist-signed-badge" title={`Signed by ${checklistData.signedBy}${checklistData.signedComment ? ` — ${checklistData.signedComment}` : ''}`}>
              ✅ Signed by {checklistData.signedBy}
            </span>
          )}
          {assessed > 0 && !isLocked && (
            <button
              className="checklist-action-btn checklist-action-btn--reset"
              onClick={resetAll}
              title="Reset all items"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* F3: Voided stamp */}
      {checklistData.voidReason && (
        <div className="checklist-void-stamp" role="alert">
          ⚠️ <strong>Voided:</strong> {checklistData.voidReason}
          {checklistData.voidAt && (
            <span className="checklist-void-date"> &mdash; {new Date(checklistData.voidAt).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
          )}
        </div>
      )}

      {/* Coverage indicator (fix #5) */}
      {coverage && (
        <div className={`checklist-coverage${coveragePct !== null && coveragePct < 60 ? ' checklist-coverage--warning' : ''}`}>
          <span className="checklist-coverage-text">
            📊 Coverage: {coverage.domains}/{coverage.totalDomains} domains · {coverage.clausesMatched} clauses matched
          </span>
          {coveragePct !== null && coveragePct < 60 && (
            <span className="checklist-coverage-warn">
              ⚠️ Low coverage — some relevant standards may not be included
            </span>
          )}
        </div>
      )}

      {/* Completeness warning (fix #6) */}
      {totalItems > 0 && totalItems < 5 && (
        <div className="checklist-completeness-warning">
          ⚠️ Only {totalItems} requirement{totalItems === 1 ? '' : 's'} retrieved — this checklist may be incomplete. Verify against the full standard before relying on it.
        </div>
      )}

      {/* Progress bar (fix #8) */}
      <div className="checklist-progress">
        <div className="checklist-progress-bar">
          <div
            className="checklist-progress-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="checklist-progress-text">
          <span>{assessed}/{totalItems} items assessed</span>
          {assessed > 0 && (
            <span className="checklist-progress-breakdown">
              {passCount > 0 && <span className="checklist-stat checklist-stat--pass">✅ {passCount}</span>}
              {failCount > 0 && <span className="checklist-stat checklist-stat--fail">❌ {failCount}</span>}
              {naCount > 0 && <span className="checklist-stat checklist-stat--na">➖ {naCount}</span>}
            </span>
          )}
          {progressPct === 100 && <span className="checklist-complete-badge">✅ Complete</span>}
        </div>
      </div>

      {/* Categories */}
      {checklistData.categories.map((cat, catIdx) => {
        const isCollapsed = collapsed.has(cat.name);
        const catAssessed = cat.items.filter((i) => i.status !== 'unchecked').length;
        const catTotal = cat.items.length;

        return (
          <div key={cat.name} className="checklist-category">
            <button
              className="checklist-category-header"
              onClick={() => toggleCollapse(cat.name)}
              aria-expanded={!isCollapsed}
            >
              <svg
                className={`checklist-chevron${isCollapsed ? ' checklist-chevron--collapsed' : ''}`}
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
              <span className="checklist-category-name">{cat.name}</span>
              <span className="checklist-category-count">
                {catAssessed}/{catTotal}
              </span>
            </button>

            {!isCollapsed && (
              <ul className="checklist-items">
                {cat.items.map((item, itemIdx) => {
                  const noteKey = `${catIdx}-${itemIdx}`;
                  const notesExpanded = expandedNotes.has(noteKey);
                  const isMandatory = item.tag === 'mandatory';
                  const isGuidance = item.tag === 'guidance';

                  return (
                    <li
                      key={itemIdx}
                      className={`checklist-item${
                        item.status === 'pass' ? ' checklist-item--pass' : ''
                      }${item.status === 'fail' ? ' checklist-item--fail' : ''
                      }${item.status === 'na' ? ' checklist-item--na' : ''
                      }${isMandatory ? ' checklist-item--mandatory' : ''
                      }${isGuidance ? ' checklist-item--guidance' : ''}`}
                    >
                      <div className="checklist-item-main">
                        {/* Tag badge (fix #4) */}
                        {isMandatory && <span className="checklist-tag checklist-tag--mandatory">Required</span>}
                        {isGuidance && <span className="checklist-tag checklist-tag--guidance">Recommended</span>}

                        <span className={`checklist-item-text${item.status === 'pass' ? ' checklist-item-text--pass' : ''}${item.status === 'na' ? ' checklist-item-text--na' : ''}`}>
                          {item.text}
                        </span>

                        <div className="checklist-item-controls">
                          {/* Segmented status control (fix #2) — F3: disabled when locked */}
                          <div className="checklist-status-group" role="group" aria-label="Item status">
                            <button
                              className={`checklist-status-btn checklist-status-btn--pass${item.status === 'pass' ? ' checklist-status-btn--active' : ''}`}
                              onClick={() => !isLocked && setItemStatus(catIdx, itemIdx, 'pass')}
                              title={isLocked ? 'Checklist is locked' : 'Pass'}
                              type="button"
                              aria-pressed={item.status === 'pass'}
                              disabled={isLocked}
                            >
                              ✅
                            </button>
                            <button
                              className={`checklist-status-btn checklist-status-btn--fail${item.status === 'fail' ? ' checklist-status-btn--active' : ''}`}
                              onClick={() => !isLocked && setItemStatus(catIdx, itemIdx, 'fail')}
                              title={isLocked ? 'Checklist is locked' : 'Fail'}
                              type="button"
                              aria-pressed={item.status === 'fail'}
                              disabled={isLocked}
                            >
                              ❌
                            </button>
                            <button
                              className={`checklist-status-btn checklist-status-btn--na${item.status === 'na' ? ' checklist-status-btn--active' : ''}`}
                              onClick={() => !isLocked && setItemStatus(catIdx, itemIdx, 'na')}
                              title={isLocked ? 'Checklist is locked' : 'N/A'}
                              type="button"
                              aria-pressed={item.status === 'na'}
                              disabled={isLocked}
                            >
                              ➖
                            </button>
                          </div>

                          {/* Notes toggle (fix #3) */}
                          <button
                            className={`checklist-notes-toggle${item.notes ? ' checklist-notes-toggle--has-notes' : ''}`}
                            onClick={() => toggleNotes(noteKey)}
                            title={notesExpanded ? 'Hide notes' : 'Add notes'}
                            type="button"
                            aria-expanded={notesExpanded}
                          >
                            📝
                          </button>

                          {/* F2: Camera/upload button — F3: hidden when locked */}
                          {item.id && checklistData.checklistId && !isLocked && (
                            <button
                              className={`checklist-camera-btn${
                                uploadingItems.has(item.id) ? ' checklist-camera-btn--uploading' : ''
                              }${
                                (evidenceMap[item.id]?.length ?? 0) > 0 ? ' checklist-camera-btn--has-evidence' : ''
                              }`}
                              onClick={() => handleCameraClick(catIdx, itemIdx)}
                              title="Attach photo evidence"
                              type="button"
                              disabled={uploadingItems.has(item.id)}
                              aria-label="Attach photo evidence"
                            >
                              {uploadingItems.has(item.id) ? (
                                <span className="checklist-upload-spinner" aria-label="Uploading" />
                              ) : (
                                <>
                                  📷
                                  {(evidenceMap[item.id]?.length ?? 0) > 0 && (
                                    <span className="checklist-camera-count">{evidenceMap[item.id].length}</span>
                                  )}
                                </>
                              )}
                            </button>
                          )}
                          {/* F3: show evidence count badge (no upload) when locked */}
                          {item.id && checklistData.checklistId && isLocked && (evidenceMap[item.id]?.length ?? 0) > 0 && (
                            <span className="checklist-camera-btn checklist-camera-btn--has-evidence" style={{ cursor: 'default', opacity: 0.7 }}>
                              📷 <span className="checklist-camera-count">{evidenceMap[item.id].length}</span>
                            </span>
                          )}

                          {/* Supplier draft email button — only for unchecked/fail items, not locked */}
                          {item.id && (item.status === 'unchecked' || item.status === 'fail') && !isLocked && (
                            <button
                              className="checklist-notes-toggle"
                              onClick={() => item.id && setSupplierDraftItem({ itemId: item.id, itemText: item.text })}
                              title="Draft supplier chase email (bilingual EN/ZH)"
                              type="button"
                              aria-label="Draft supplier chase email"
                            >
                              📧
                            </button>
                          )}

                          {/* Reference chip */}
                          {(item.standard || item.clause) && (
                            <span
                              className="checklist-ref-chip"
                              role={onCrossRef ? 'link' : undefined}
                              tabIndex={onCrossRef ? 0 : undefined}
                              onClick={() => {
                                if (onCrossRef) {
                                  const q = item.clause
                                    ? `${item.standard} Clause ${item.clause}`
                                    : item.standard;
                                  onCrossRef(q);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (onCrossRef && (e.key === 'Enter' || e.key === ' ')) {
                                  e.preventDefault();
                                  const q = item.clause
                                    ? `${item.standard} Clause ${item.clause}`
                                    : item.standard;
                                  onCrossRef(q);
                                }
                              }}
                              title={`Look up ${item.standard}${item.clause ? ` Clause ${item.clause}` : ''}`}
                            >
                              {item.standard}{item.clause ? ` §${item.clause}` : ''}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Expandable notes field (fix #3) — F3: readOnly when locked */}
                      {notesExpanded && (
                        <div className="checklist-notes-area">
                          <textarea
                            className="checklist-notes-input"
                            value={item.notes}
                            onChange={(e) => !isLocked && setItemNotes(catIdx, itemIdx, e.target.value)}
                            placeholder={isLocked ? '(locked — read only)' : 'Add observations, evidence references, or exemption notes…'}
                            rows={2}
                            readOnly={isLocked}
                          />
                        </div>
                      )}

                      {/* F2: Evidence thumbnail strip */}
                      {item.id && (evidenceMap[item.id]?.length ?? 0) > 0 && (
                        <div className="checklist-evidence-strip">
                          {evidenceMap[item.id].map((ev, evIdx) => (
                            <div key={ev.id} className="checklist-evidence-thumb-wrap">
                              <button
                                className="checklist-evidence-thumb"
                                onClick={() => setLightbox({
                                  itemId: item.id!,
                                  evidenceList: evidenceMap[item.id!],
                                  index: evIdx,
                                })}
                                type="button"
                                title={ev.caption || ev.filename || 'View photo'}
                                aria-label={`View photo ${evIdx + 1}`}
                              >
                                <img
                                  src={ev.url}
                                  alt={ev.caption || ev.filename || 'Evidence'}
                                  className="checklist-evidence-thumb-img"
                                />
                              </button>
                              {/* F3: hide delete button when locked */}
                              {!isLocked && (
                                <button
                                  className="checklist-evidence-delete-btn"
                                  onClick={() => handleEvidenceDelete(item.id!, ev.id)}
                                  type="button"
                                  title="Delete photo"
                                  aria-label="Delete photo"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}

      {/* F3: Audit trail — shown when there are entries */}
      {auditEntries.length > 0 && (
        <div className="checklist-audit">
          <button
            className="checklist-audit-toggle"
            onClick={() => setAuditExpanded((p) => !p)}
            type="button"
            aria-expanded={auditExpanded}
          >
            📖 History ({auditEntries.length})
            <span className={`checklist-chevron${auditExpanded ? '' : ' checklist-chevron--collapsed'}`} style={{ marginLeft: 6 }}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </button>
          {auditExpanded && (
            <ul className="checklist-audit-list">
              {auditEntries.map((entry) => (
                <li key={entry.id} className={`checklist-audit-entry checklist-audit-entry--${entry.action}`}>
                  <span className="checklist-audit-icon">
                    {entry.action === 'signed' ? '✅' : '⚠️'}
                  </span>
                  <span className="checklist-audit-text">
                    <strong>{entry.action === 'signed' ? 'Signed' : 'Voided'}</strong>
                    {entry.actor_name && ` by ${entry.actor_name}`}
                    {entry.detail && <em> — &ldquo;{entry.detail}&rdquo;</em>}
                    <span className="checklist-audit-date">
                      {' '}&mdash;{' '}
                      {new Date(entry.created_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Disclaimer */}
      <p className="checklist-disclaimer">
        ⚠️ This is a research summary, not a formal compliance checklist.
        Verify all requirements with a qualified professional before relying on this information for compliance purposes.
      </p>

      {/* Upload error */}
      {uploadError && (
        <div className="checklist-upload-error" role="alert">
          ⚠️ {uploadError}
          <button onClick={() => setUploadError(null)} type="button" className="checklist-upload-error-close">✕</button>
        </div>
      )}

      {/* Export generating indicator */}
      {exportGenerating && (
        <div className="checklist-export-generating" role="status">
          <span className="checklist-upload-spinner" />
          Preparing export with photo evidence…
        </div>
      )}

      {/* F2: Hidden file input for camera/photo upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelected}
        style={{ display: 'none' }}
        aria-hidden="true"
      />

      {/* Export modal */}
      {showExportModal && (
        <ExportModal
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
        />
      )}

      {/* F2: Lightbox modal */}
      {lightbox && (
        <LightboxModal
          evidenceList={lightbox.evidenceList}
          index={lightbox.index}
          itemId={lightbox.itemId}
          onClose={() => setLightbox(null)}
          onPrev={() => setLightbox((prev) =>
            prev ? { ...prev, index: (prev.index - 1 + prev.evidenceList.length) % prev.evidenceList.length } : null
          )}
          onNext={() => setLightbox((prev) =>
            prev ? { ...prev, index: (prev.index + 1) % prev.evidenceList.length } : null
          )}
          onDelete={handleEvidenceDelete}
          readOnly={isLocked}
        />
      )}

      {/* F3: Sign modal */}
      {signModalOpen && (
        <SignModal
          onClose={() => setSignModalOpen(false)}
          onSign={handleSign}
          busy={signBusy}
          error={signError}
        />
      )}

      {/* F3: Void modal */}
      {voidModalOpen && (
        <VoidModal
          onClose={() => setVoidModalOpen(false)}
          onVoid={handleVoid}
          busy={voidBusy}
          error={voidError}
        />
      )}

      {/* Supplier draft modal */}
      {supplierDraftItem && (
        <SupplierDraftModal
          initialProductName={checklistData.title || ''}
          initialMissingItems={[supplierDraftItem.itemText]}
          checklistItemId={supplierDraftItem.itemId}
          onClose={() => setSupplierDraftItem(null)}
        />
      )}
    </div>
  );
}
